import { action, observable, computed } from 'mobx';
import { SystemFeeRefundPolicy } from '@link/share/services/const';
import { supplierAccountApi } from '@link/supplier/services';
import { ListSuppliersStatusEnum } from '@link/supplier/services/const';
import {
  AccountbaseSupplier,
  SupplierBrief,
  ListSuppliersRequest,
  Supplier as ISupplier
} from '@link/supplier/services/types';
import auth from './auth';

const SELECTED_SUPPLIER_ID_KEY = 'SELECTED_SUPPLIER_ID';

const SELECTED_CUSTOMER_ID_KEY = 'SELECTED_CUSTOMER_ID';

export class Supplier {
  @observable accessor supplier: AccountbaseSupplier | null = null;
  @observable accessor loading: boolean = false;
  @observable accessor suppliersLoaded = false;
  @observable accessor suppliers: SupplierBrief[] = [];
  @observable accessor currentActivitySupplier: ISupplier | null = null;
  @observable accessor selectSupplierId = '';
  @observable accessor selectCustomerId = '';
  @observable accessor sessionStorageInitialized = false;

  @action
  async getDetail(supplierId?: string) {
    const targetSupplierId = supplierId || this.selectSupplierId;
    if (!targetSupplierId) {
      return;
    }
    this.loading = true;
    const response = await supplierAccountApi.getSupplierDetail({
      supplierId: targetSupplierId
    });
    if (response.supplier) {
      this.setSupplier(response.supplier);
      this.setSelectCustomerId(response.supplier.supplierCustomId || '');
    }
    this.setLoading(false);
  }
  @action
  setSupplier(supplier: AccountbaseSupplier) {
    this.supplier = supplier;
  }
  @action
  setLoading(loading: boolean) {
    this.loading = loading;
  }
  @computed
  get originalName() {
    return this.supplier?.originalName;
  }
  @computed
  get supplierCustomId() {
    return this.supplier?.supplierCustomId;
  }
  @action
  setSuppliers(suppliers: SupplierBrief[]) {
    this.suppliers = suppliers;
  }
  @action
  setSuppliersLoaded(loaded: boolean) {
    this.suppliersLoaded = loaded;
  }
  @action
  async getSuppliers(params?: ListSuppliersRequest) {
    // 只有 Link Admin 才能获取所有供应商列表
    if (!auth.isLinkAdmin) {
      // 非 Link Admin 时，加载当前用户的供应商信息用于 ID 转换
      if (auth.userSupplierId && !this.suppliersLoaded) {
        try {
          const response = await supplierAccountApi.getSupplierDetail({
            supplierId: auth.userSupplierId
          });
          if (response.supplier) {
            const supplierBrief: SupplierBrief = {
              supplierId: response.supplier.supplierId,
              supplierCustomId: response.supplier.supplierCustomId,
              originalName: response.supplier.originalName,
              status: response.supplier.status
            };
            this.setSuppliers([supplierBrief]);
          }
        } catch {
          this.setSuppliers([]);
        }
      }
      this.setSuppliersLoaded(true);
      return { suppliers: [] };
    }

    const response = await supplierAccountApi.listSuppliers({
      status: [ListSuppliersStatusEnum.On],
      ...params
    });
    this.setSuppliers(response.suppliers || []);
    this.setSuppliersLoaded(true);
    return response;
  }

  @computed
  get supplierOpts() {
    return this.suppliers.map(supplier => ({
      label: `${supplier.supplierCustomId}(${supplier.originalName})`,
      value: supplier.supplierId as string
    }));
  }

  //因为新的supplierlist获取接口拿到的都是int类型的id，但是老的cms接口都需要'LINKTIVITY'这种string类型的id，所以通过这个方法转换
  @action
  getSupplierCustomIdBySupplierId(supplierId: string) {
    return (
      this.suppliers.find(supplier => supplier.supplierId === supplierId)
        ?.supplierCustomId || ''
    );
  }
  //因为cms的老接口拿到的supplierId都是返回'LINKTIVITY'这种，但是新的supplierList选择器有需要新的int类型id，所以通过这个方法转换
  @action
  getSupplierIdBySupplierCustomId(SupplierCustomId: string) {
    return (
      this.suppliers.find(
        supplier => supplier.supplierCustomId === SupplierCustomId
      )?.supplierId || ''
    );
  }

  @action
  setCurrentActivitySupplier(supplier: ISupplier) {
    this.currentActivitySupplier = supplier;
  }

  @action
  async getActivitySupplier(supplierIdOrCustomId: string) {
    // 支持传入 supplierId (数字型) 或 supplierCustomId (字符串型如 'LINKTIVITY')
    let supplierId = supplierIdOrCustomId;
    let supplierCustomId = supplierIdOrCustomId;

    // 如果不是纯数字，说明传入的是 supplierCustomId，需要转换为 supplierId
    if (!/^\d+$/.test(supplierIdOrCustomId)) {
      const found = this.getSupplierIdBySupplierCustomId(supplierIdOrCustomId);
      if (found) {
        supplierId = found;
      } else {
        // 如果找不到，可能 suppliers 列表还未加载，先加载
        if (!this.suppliersLoaded) {
          await this.getSuppliers();
          const foundAfterLoad =
            this.getSupplierIdBySupplierCustomId(supplierIdOrCustomId);
          if (foundAfterLoad) {
            supplierId = foundAfterLoad;
          }
        }
      }
    } else {
      // 传入的是数字 ID，需要转换为 supplierCustomId 用于缓存比较
      const found = this.getSupplierCustomIdBySupplierId(supplierIdOrCustomId);
      if (found) {
        supplierCustomId = found;
      } else {
        // 如果找不到，可能 suppliers 列表还未加载，先加载
        if (!this.suppliersLoaded) {
          await this.getSuppliers();
          const foundAfterLoad =
            this.getSupplierCustomIdBySupplierId(supplierIdOrCustomId);
          if (foundAfterLoad) {
            supplierCustomId = foundAfterLoad;
          }
        }
      }
    }

    // 使用 supplierCustomId 进行缓存比较，因为 currentActivitySupplier.id 存储的是 supplierCustomId
    if (this.currentActivitySupplier?.id !== supplierCustomId) {
      if (!supplierId) {
        return;
      }
      const response = await supplierAccountApi.getSupplierDetail({
        supplierId
      });
      if (response.supplier) {
        // 将新接口的 AccountbaseSupplier 转换为旧的 ISupplier 格式
        const supplier: ISupplier = {
          id: response.supplier.supplierCustomId || '',
          name: response.supplier.originalName || '',
          directPublish: response.supplier.allowDirectPublish || false
        };
        this.setCurrentActivitySupplier(supplier);
        return response.supplier;
      }
    }
  }

  @action
  setSelectSupplierId(supplierId: string) {
    this.selectSupplierId = supplierId;
    if (supplierId) {
      sessionStorage.setItem(SELECTED_SUPPLIER_ID_KEY, supplierId);
    } else {
      sessionStorage.removeItem(SELECTED_SUPPLIER_ID_KEY);
    }
  }

  @action
  setSelectCustomerId(customerId: string) {
    this.selectCustomerId = customerId;
    if (customerId) {
      sessionStorage.setItem(SELECTED_CUSTOMER_ID_KEY, customerId);
    } else {
      sessionStorage.removeItem(SELECTED_CUSTOMER_ID_KEY);
    }
  }
  @action
  clearSelectSupplierInfo() {
    this.setSelectSupplierId('');
    this.setSelectCustomerId('');
    this.sessionStorageInitialized = false;
  }

  @action
  async initFromSessionStorage() {
    if (this.sessionStorageInitialized) return;
    this.sessionStorageInitialized = true;
    // @action only covers sync code before the first await; mutations after awaits use @action methods.
    const savedSupplierId = sessionStorage.getItem(SELECTED_SUPPLIER_ID_KEY);

    if (savedSupplierId) {
      const isValidSupplierId = this.suppliers.some(
        s => s.supplierId === savedSupplierId
      );

      if (isValidSupplierId) {
        this.selectSupplierId = savedSupplierId;
        await this.getDetail(savedSupplierId); // sets selectCustomerId from API response
        return;
      }
    }

    // No valid saved supplier, fall back to first supplier
    if (this.suppliers.length > 0) {
      const firstSupplierId = this.suppliers[0].supplierId || '';
      if (firstSupplierId) {
        this.setSelectSupplierId(firstSupplierId);
        this.setSelectCustomerId(
          this.getSupplierCustomIdBySupplierId(firstSupplierId)
        );
      }
    }
  }

  @computed
  get isCustomerSystemFeeFullRefund() {
    return (
      this.supplier?.systemFeeRefundPolicyCustomer ===
      SystemFeeRefundPolicy.AllRefund
    );
  }
}

export default new Supplier();
